import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { userLikeSchema } from './user.js';

export const substitutionRequestTypeSchema = z.enum(['requested_sub_by_coach', 'coach_no_show', 'assigned_by_admin']);
export type SubstitutionRequestType = z.infer<typeof substitutionRequestTypeSchema>;
export const SubstitutionRequestType = substitutionRequestTypeSchema.enum;

export const SubstitutionRequestTypeLabelMap: Record<SubstitutionRequestType, string> = {
  [SubstitutionRequestType.requested_sub_by_coach]: 'Requested by coach',
  [SubstitutionRequestType.coach_no_show]: 'Coach no show',
  [SubstitutionRequestType.assigned_by_admin]: 'Assigned by admin',
};

export const substitutionRequestStatusSchema = z.nativeEnum(PrismaEnums.SubstitutionRequestStatus);
export type SubstitutionRequestStatus = z.infer<typeof substitutionRequestStatusSchema>;
export const SubstitutionRequestStatus = substitutionRequestStatusSchema.enum;

export const substitutionRequestOriginSchema = z.enum(['manual', 'automatic_conflict']);
export type SubstitutionRequestOrigin = z.infer<typeof substitutionRequestOriginSchema>;
export const SubstitutionRequestOrigin = substitutionRequestOriginSchema.enum;

export const substitutionRequestSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  requestedById: z.string(),
  requestedByCoach: z.lazy(() => userLikeSchema),
  substituteId: z.string().nullable(),
  substituteCoach: z.lazy(() => userLikeSchema).nullable(),
  requestReason: z.string().nullable(),
  status: substitutionRequestStatusSchema,
  type: substitutionRequestTypeSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
  hoursUntilSessionStart: z.number(),
  acceptedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
  origin: substitutionRequestOriginSchema,
});

export type SubstitutionRequest = z.infer<typeof substitutionRequestSchema>;

/** Scalar fields only — sufficient for {@link getSessionSubstitutionRequestSummary}. */
export const coachPortalSubstitutionRequestSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  requestedById: z.string(),
  substituteId: z.string().nullable(),
  status: substitutionRequestStatusSchema,
  type: substitutionRequestTypeSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  hoursUntilSessionStart: z.number(),
  acceptedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  origin: substitutionRequestOriginSchema,
});

export type CoachPortalSubstitutionRequest = z.infer<typeof coachPortalSubstitutionRequestSchema>;

export const createSubstitutionRequestByCoachSchema = z.object({
  sessionId: z.string(),
  requestReason: z.string().nullable().optional(),
});
export type CreateSubstitutionByCoachRequest = z.infer<typeof createSubstitutionRequestByCoachSchema>;

/** Teacher Portal "Request a substitute" reason field. */
export const SUBSTITUTION_REQUEST_REASON_MAX_LENGTH = 150;
export const SUBSTITUTION_REQUEST_REASON_REQUIRED_MESSAGE = 'A reason is required.';

/** Trim and drop empty / whitespace-only reasons. */
export function normalizeSubstitutionRequestReason(reason: string | null | undefined): string | undefined {
  const trimmed = reason?.trim();
  return trimmed ? trimmed : undefined;
}

export type ResolveSubstitutionRequestReasonResult =
  | { ok: true; requestReason: string | undefined }
  | { ok: false; message: string };

/**
 * Normalize a coach-submitted sub-request reason. When `required` is true (attendance policy V2),
 * missing or whitespace-only input fails; otherwise empty becomes `undefined` (optional).
 */
export function resolveSubstitutionRequestReason(
  reason: string | null | undefined,
  { required }: { required: boolean },
): ResolveSubstitutionRequestReasonResult {
  const requestReason = normalizeSubstitutionRequestReason(reason);

  if (required && requestReason === undefined) {
    return { ok: false, message: SUBSTITUTION_REQUEST_REASON_REQUIRED_MESSAGE };
  }

  return { ok: true, requestReason };
}

/** Zod schema for the Teacher Portal Request a substitute modal. */
export function requestSubstituteConfirmDialogSchema(reasonRequired: boolean) {
  const reasonMessage = `Reason must be ${SUBSTITUTION_REQUEST_REASON_MAX_LENGTH} characters or fewer.`;
  const requestReason = reasonRequired
    ? z
        .string()
        .trim()
        .min(1, SUBSTITUTION_REQUEST_REASON_REQUIRED_MESSAGE)
        .max(SUBSTITUTION_REQUEST_REASON_MAX_LENGTH, reasonMessage)
    : z.string().max(SUBSTITUTION_REQUEST_REASON_MAX_LENGTH, reasonMessage);

  return z.object({ requestReason });
}

export type CoachSubstitutionInvolvement =
  | 'requested_req_pending'
  | 'requested_req_fulfilled'
  | 'fulfilled_request'
  | 'none';
